/* eslint-disable max-len */
import React, { useState, useMemo } from 'react'
import moment from 'moment'
import { Col, Nav, Row, Tab } from 'react-bootstrap'

import TodayCard from './TodayCard.component'
import DashboardTable from '../DashboardTable.component'
import { DashboardUnit } from '@dataTypes/dashboard.types'
import { PatientCard, TodaySideModal } from '@components/layout/Modals'
import { formatNumber } from '@utils/index'
import { Patient } from '@dataTypes/PatientInfo.types'
import { mockPatients } from '@assets/mockdata/Patient/mockPatients'
import {
  scheduleByLocation,
  scheduleByProvider,
  todayCard,
  todayCardConfig,
  todaySideModal,
} from '../DashboardMockData'

import '../DashboardStyle.css'

function Today(): React.ReactElement {
  const [selectedValue, setSelectedValue] = useState({ field: '', value: '' })
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isPatCardOpen, setIsPatCardOpen] = useState(false)
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null)

  const dateYesterday = moment().format('dddd MMMM Do')
  const patCount = mockPatients.length
  const totalProd = 20000
  const goal = 10000

  // useMemo for deriving dropdown options and filtering data
  const providerDataOption = useMemo(
    () => [
      'All Providers',
      ...Array.from(new Set(scheduleByProvider.map((item) => item.provider))),
    ],
    [],
  )
  const locationDataOption = useMemo(
    () => [
      'All Locations',
      ...Array.from(new Set(scheduleByLocation.map((item) => item.location))),
    ],
    [],
  )

  const filteredProviderData = useMemo(() => {
    if (
      selectedValue.field === 'provider' &&
      selectedValue.value !== 'All Providers'
    ) {
      return scheduleByProvider.filter(
        (item) => item.provider === selectedValue.value,
      )
    }
    return scheduleByProvider
  }, [selectedValue])

  const filteredLocationData = useMemo(() => {
    if (
      selectedValue.field === 'location' &&
      selectedValue.value !== 'All Locations'
    ) {
      return scheduleByLocation.filter(
        (item) => item.location === selectedValue.value,
      )
    }
    return scheduleByLocation
  }, [selectedValue])

  const toggleModal = (): void => setIsModalOpen(!isModalOpen)

  const togglePatModal = (patient?: Patient): void => {
    setSelectedPatient(patient || null)
    setIsPatCardOpen(!isPatCardOpen)
    setIsModalOpen(false)
  }

  const handleSelect = (field: string, value: string): void =>
    setSelectedValue({ field, value })

  const goalTemplate = (data: any): React.ReactElement => {
    const isBelow = data.goal > data.actual ? true : false
    const formatGoal = formatNumber(data.goal, true)
    const formatActual = formatNumber(data.actual, true)
    return (
      <div>
        <span>{`${formatGoal}-${formatActual}`}</span>&nbsp;
        <span>{`${isBelow ? 'below goal' : 'above goal'}`}</span>
      </div>
    )
  }

  const currencyTemplate = (data: any, field: string): React.ReactElement => {
    const value = data[field]
    const formattedValue = formatNumber(value, true)

    return <div>{formattedValue}</div>
  }

  const providerCol = (data: any, field: string): React.ReactElement => {
    const value = data[field]
    return (
      <div>
        <span>
          <i className="bi bi-circle-fill me-1 circle-icon-fill"></i>
        </span>
        &nbsp;{value}
      </div>
    )
  }

  const columnByProvider = [
    {
      field: 'provider',
      header: 'Provider',
      template: (data: any) => providerCol(data, 'provider'),
      isDropdown: true,
    },
    {
      field: 'appointments',
      header: 'Appointments',
    },
    {
      field: 'hours',
      header: 'Hours',
    },
    {
      field: 'scheduled',
      header: 'Scheduled',
      template: (data: any) => currencyTemplate(data, 'scheduled'),
    },
    {
      field: 'completed',
      header: 'Completed',
      template: (data: any) => currencyTemplate(data, 'completed'),
    },
    {
      field: 'goal',
      header: 'Goal',
      template: goalTemplate,
    },
    {
      field: 'unscheduledTx',
      header: 'Unscheduled Tx',
      template: (data: any) => currencyTemplate(data, 'unscheduledTx'),
    },
  ]

  const columnByLocation = [
    {
      field: 'location',
      header: 'Location',
      template: (data: any) => providerCol(data, 'location'),
      isDropdown: true,
    },
    {
      field: 'appointments',
      header: 'Appointments',
    },
    {
      field: 'hours',
      header: 'Hours',
    },
    {
      field: 'scheduled',
      header: 'Scheduled',
      template: (data: any) => currencyTemplate(data, 'scheduled'),
    },
    {
      field: 'completed',
      header: 'Completed',
      template: (data: any) => currencyTemplate(data, 'completed'),
    },
    {
      field: 'goal',
      header: 'Goal',
      template: goalTemplate,
    },
    {
      field: 'unscheduledTx',
      header: 'Unscheduled Tx',
      template: (data: any) => currencyTemplate(data, 'unscheduledTx'),
    },
  ]

  return (
    <>
      <Row className="mt-3 p-3 pt-1 pb-1">
        <Col md={12} className="text-black">
          <span className="fs-1 fw-bolder me-2">Today</span>
          <span className="fs-3">{dateYesterday}</span>
        </Col>
      </Row>
      <Row className="p-3 pt-0 pb-1">
        <Col md={12}>
          <span className="display-6 text-blue1 fw-semibold">
            {` Here's what's going on Today!`}
          </span>
        </Col>
      </Row>
      <Row className="p-3 pt-0 pb-1">
        <Col md={12} className="fs-1 text-blue1 fw-bold">
          You have&nbsp;
          <button
            type="button"
            className="unstyled-button text-decoration-underline"
            onClick={toggleModal}
          >
            {`${patCount} patients`}
          </button>
          &nbsp;
          {`coming in today, totaling ${formatNumber(totalProd, true)} in scheduled production, which is`}
          &nbsp;
          <span className="text-blue">{` ${formatNumber(goal, true)} above goal`}</span>
          .
        </Col>
      </Row>
      <Row className="mt-2">
        {todayCard.map((item) => (
          <Col md={3} key={item.category}>
            <TodayCard
              dataId={item.category}
              value={item.value}
              unit={item.unit as DashboardUnit}
              patientCount={item.patientCount}
              cardConfig={todayCardConfig}
            />
          </Col>
        ))}
      </Row>
      <Row className="mt-4">
        <Col md={12} className="morning-huddle-today-nav">
          <Tab.Container defaultActiveKey="0">
            <Col md={12} className="p-0">
              <Nav variant="pills">
                <Nav.Item>
                  <Nav.Link eventKey="0">Schedule By Provider</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="1">Schedule By Location</Nav.Link>
                </Nav.Item>
              </Nav>
              <Tab.Content>
                <Tab.Pane eventKey="0">
                  <DashboardTable
                    title="Provider"
                    data={filteredProviderData}
                    columns={columnByProvider}
                    allowSorting={false}
                    allowMultiSorting={false}
                    filterSelect={handleSelect}
                    currFilterValue={selectedValue}
                    dropdownOptions={providerDataOption}
                  />
                </Tab.Pane>
                <Tab.Pane eventKey="1">
                  <DashboardTable
                    title="Location"
                    data={filteredLocationData}
                    columns={columnByLocation}
                    allowSorting={false}
                    allowMultiSorting={false}
                    filterSelect={handleSelect}
                    currFilterValue={selectedValue}
                    dropdownOptions={locationDataOption}
                  />
                </Tab.Pane>
              </Tab.Content>
            </Col>
          </Tab.Container>
        </Col>
      </Row>
      <TodaySideModal
        isOpen={isModalOpen}
        toggleModal={toggleModal}
        title={`${mockPatients.length} Patients`}
        data={todaySideModal}
        isRowClickable={true}
        onClick={() => togglePatModal(mockPatients[0])}
      />
      <PatientCard
        show={isPatCardOpen}
        onHide={togglePatModal}
        patient={selectedPatient || mockPatients[0]}
      />
    </>
  )
}

export default Today
